import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { findAnchorPositions, positionsStillMatch } from './findAnchor';

export interface ThreadAnchor {
  id: string;
  /** Original positions captured at comment creation. May be stale across
   *  master/draft swaps; treated as a fast-path hint. */
  from: number;
  to: number;
  /** Text content the comment was anchored to. Authoritative for finding
   *  the comment's range in whatever doc is currently being rendered. */
  anchor_text: string;
  status: 'open' | 'resolved' | 'orphaned';
}

export const commentsPluginKey = new PluginKey<DecorationSet>('hsp-comment-decorations');

interface SetThreadsMeta {
  type: 'set-threads';
  threads: ThreadAnchor[];
  activeThreadId: string | null;
}

interface SetActiveMeta {
  type: 'set-active';
  activeThreadId: string | null;
}

type Meta = SetThreadsMeta | SetActiveMeta;

/**
 * Resolve a thread's range in the current doc. Try the stored positions
 * first (fast); fall back to text search by anchor_text. Return null if
 * the snippet is no longer present in the doc — caller treats this as
 * an orphaned thread (visible in sidebar, no inline highlight).
 */
function resolveThreadRange(
  doc: import('@tiptap/pm/model').Node,
  thread: ThreadAnchor,
): { from: number; to: number } | null {
  if (positionsStillMatch(doc, thread.from, thread.to, thread.anchor_text)) {
    return { from: thread.from, to: thread.to };
  }
  return findAnchorPositions(doc, thread.anchor_text);
}

function buildDecorationSet(
  doc: import('@tiptap/pm/model').Node,
  threads: ThreadAnchor[],
  activeThreadId: string | null,
): DecorationSet {
  const decos: Decoration[] = [];
  for (const t of threads) {
    const range = resolveThreadRange(doc, t);
    if (!range) continue;
    const isActive = t.id === activeThreadId;
    const classes = ['comment-anchor', `comment-${t.status}`];
    if (isActive) classes.push('comment-active');
    decos.push(
      Decoration.inline(range.from, range.to, {
        class: classes.join(' '),
        'data-thread-id': t.id,
      }),
    );
  }
  return DecorationSet.create(doc, decos);
}

interface PluginState {
  threads: ThreadAnchor[];
  activeThreadId: string | null;
  decoSet: DecorationSet;
}

export const CommentDecorations = Extension.create({
  name: 'commentDecorations',

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginState>({
        key: commentsPluginKey as unknown as PluginKey<PluginState>,
        state: {
          init: (): PluginState => ({
            threads: [],
            activeThreadId: null,
            decoSet: DecorationSet.empty,
          }),
          apply(tr, prev): PluginState {
            const meta = tr.getMeta(commentsPluginKey) as Meta | undefined;
            if (meta?.type === 'set-threads') {
              return {
                threads: meta.threads,
                activeThreadId: meta.activeThreadId,
                decoSet: buildDecorationSet(tr.doc, meta.threads, meta.activeThreadId),
              };
            }
            if (meta?.type === 'set-active') {
              return {
                threads: prev.threads,
                activeThreadId: meta.activeThreadId,
                decoSet: buildDecorationSet(tr.doc, prev.threads, meta.activeThreadId),
              };
            }
            // Doc changed — re-resolve anchors so comments stay aligned with
            // the moved/edited text. We rebuild rather than map() because
            // anchor_text search is the source of truth.
            if (tr.docChanged) {
              return {
                ...prev,
                decoSet: buildDecorationSet(tr.doc, prev.threads, prev.activeThreadId),
              };
            }
            return prev;
          },
        },
        props: {
          decorations(state) {
            const pluginState = (commentsPluginKey as unknown as PluginKey<PluginState>).getState(
              state,
            );
            return pluginState?.decoSet ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});

export function setThreadDecorations(
  view: import('@tiptap/pm/view').EditorView,
  threads: ThreadAnchor[],
  activeThreadId: string | null = null,
): void {
  view.dispatch(
    view.state.tr.setMeta(commentsPluginKey, {
      type: 'set-threads',
      threads,
      activeThreadId,
    } satisfies SetThreadsMeta),
  );
}

export function setActiveThread(
  view: import('@tiptap/pm/view').EditorView,
  activeThreadId: string | null,
): void {
  view.dispatch(
    view.state.tr.setMeta(commentsPluginKey, {
      type: 'set-active',
      activeThreadId,
    } satisfies SetActiveMeta),
  );
}
